import _, {flow, identity, isEqual} from 'lodash';

import {collectEnumValues, makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {surveyTemplateVariable} from '../../components/inboxes/composer/signatures/constants';
import {buildTeammateDisplayName} from '../../helpers/modelHelpers';
import {getRecipientName} from '../../helpers/models/recipientHelpers';
import {NameParts, splitName} from '../../helpers/nameSplitter';
import {CardModel} from '../../models/api/cardModel';
import {ContactModel, ContactSourcesEnum} from '../../models/api/contactModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {buildPublicId} from '../../models/applications/publicIdTypes';
import {processTextInDirtyHtml} from './processTextInDirtyHtml';

/*
 * Constants.
 */

const fallbackDelimiters = '\'"\u2018\u2019\u201C\u201D';
const fallbackRegexpPart = `\\|\\s*[${fallbackDelimiters}]([^${fallbackDelimiters}\n\r]*?)[${fallbackDelimiters}]\\s*`;
export const variableRegexp = new RegExp(`{{\\s*([^|\\s]+?)\\s*(?:${fallbackRegexpPart})?}}`, 'g');
export const partialVariableRegexp = new RegExp(
  `\\B{{([A-Za-z0-9_.]*)\\s*((?=\\|)\\|\\s*[${fallbackDelimiters}]*([^${fallbackDelimiters}]*)[${fallbackDelimiters}]*|)\\s*(\\}{0,2})$`,
);

export enum StaticVariableNamesEnum {
  CONVERSATION_ID = 'conversation.id',
  CONVERSATION_PUBLIC_ID = 'conversation.public_id',
  MESSAGE_ID = 'message.id',
  MESSAGE_PUBLIC_ID = 'message.public_id',
  RECIPIENT_HANDLE = 'recipient.handle',
  RECIPIENT_EMAIL = 'recipient.email',
  RECIPIENT_TWITTER = 'recipient.twitter',
  RECIPIENT_PHONE = 'recipient.phone',
  RECIPIENT_NAME = 'recipient.name',
  RECIPIENT_GIVEN_NAME = 'recipient.first_name',
  RECIPIENT_FAMILY_NAME = 'recipient.last_name',
  RECIPIENT_LINK = 'recipient.link',
  SURVEY = 'survey',
  USER_ID = 'user.id',
  USER_NAME = 'user.name',
  USER_GIVEN_NAME = 'user.first_name',
  USER_FAMILY_NAME = 'user.last_name',
  USER_EMAIL = 'user.email',
}
export const isStaticVariable = makeEnumTypeGuard(StaticVariableNamesEnum);
export const staticVariables = collectEnumValues(StaticVariableNamesEnum);

const publicIdPlaceholders = new Map([
  ['msg', StaticVariableNamesEnum.MESSAGE_PUBLIC_ID],
  ['cnv', StaticVariableNamesEnum.CONVERSATION_PUBLIC_ID],
]);

const publicIdPlaceholderRegexp = new RegExp(`\\b(${[...publicIdPlaceholders.keys()].join('|')})_0\\b`, 'g');

/*
 * Types.
 */

type InterpolatedValue = string | number | undefined;

export type VariableInterpolationMap = {[key: string]: InterpolatedValue};

type InterpolateOptions = {
  shouldReplacePublicIdPlaceholders?: boolean;
};

/*
 * Class Definition.
 */

export class VariableInterpolator {
  protected readonly variables: VariableInterpolationMap;

  constructor(
    user: TeammateModel,
    conversationId: number,
    messageId: number,
    recipient: VisualRecipientModel | undefined,
    recipientContacts: ContactModel[] = [],
    customAttributes: VariableInterpolationMap = {},
  ) {
    this.variables = {
      ...getVariablesFromConversation(conversationId),
      ...getVariablesFromMessage(messageId),
      ...getVariablesFromUser(user),
      ...getVariablesFromRecipient(recipient, recipientContacts),
      ...getVariablesFromSurvey(),
      ...customAttributes,
    };
  }

  interpolateDirtyHtml(dirtyHtml: string, {shouldReplacePublicIdPlaceholders}: InterpolateOptions = {}) {
    const {variables} = this;
    const interpolateDirtyHtmlToken = (dirtyHtmlToken: string) =>
      dirtyHtmlToken.replace(variableRegexp, (_match, variableName, fallbackValue) =>
        _.escape(variables[variableName] || fallbackValue || ''),
      );

    const processDirtyHtmlToken = flow(
      shouldReplacePublicIdPlaceholders ? replacePublicIdPlaceholders : identity<string>,
      interpolateDirtyHtmlToken,
    );

    try {
      return processTextInDirtyHtml(dirtyHtml, processDirtyHtmlToken);
    } catch (error) {
      return dirtyHtml;
    }
  }

  interpolateText(text: string, {shouldReplacePublicIdPlaceholders}: InterpolateOptions = {}) {
    const textWithPublicIds = shouldReplacePublicIdPlaceholders ? replacePublicIdPlaceholders(text) : text;

    return textWithPublicIds.replace(
      variableRegexp,
      (_match, variableName, fallbackValue) => this.variables[variableName] || fallbackValue || '',
    );
  }

  isEqual(interpolator: VariableInterpolator) {
    return isEqual(this.variables, interpolator.variables);
  }

  static hasRawVariables(text: string) {
    return text.match(variableRegexp) || text.match(publicIdPlaceholderRegexp);
  }
}

/*
 * Helpers.
 */

function replacePublicIdPlaceholders(dirtyHtmlOrText: string) {
  return dirtyHtmlOrText.replace(
    publicIdPlaceholderRegexp,
    (match) => `{{${publicIdPlaceholders.get(match.split('_')[0])}}}`,
  );
}

function getVariablesFromConversation(conversationId: number): VariableInterpolationMap {
  return {
    [StaticVariableNamesEnum.CONVERSATION_ID]: conversationId,
    [StaticVariableNamesEnum.CONVERSATION_PUBLIC_ID]: buildPublicId('conversation', conversationId),
  };
}

function getVariablesFromMessage(messageId: number): VariableInterpolationMap {
  return {
    [StaticVariableNamesEnum.MESSAGE_ID]: messageId,
    [StaticVariableNamesEnum.MESSAGE_PUBLIC_ID]: buildPublicId('message', messageId),
  };
}

function getVariablesFromUser(user: TeammateModel): VariableInterpolationMap {
  return {
    [StaticVariableNamesEnum.USER_ID]: user.id,
    [StaticVariableNamesEnum.USER_NAME]: buildTeammateDisplayName(user),
    [StaticVariableNamesEnum.USER_GIVEN_NAME]: user.givenName,
    [StaticVariableNamesEnum.USER_FAMILY_NAME]: user.familyName,
    [StaticVariableNamesEnum.USER_EMAIL]: user.email,
  };
}

function getVariablesFromRecipient(
  recipient: VisualRecipientModel | undefined,
  recipientContacts?: ContactModel[],
): VariableInterpolationMap {
  if (!recipient) {
    return {};
  }

  const variablesFromRecipients: VariableInterpolationMap = {
    [StaticVariableNamesEnum.RECIPIENT_HANDLE]: recipient.contact.handle,
    ...getVariablesForRecipientNames(recipient),
    ...getVariablesForRecipientLinks(recipient),
    ...getVariablesFromOtherContacts(recipient.card, recipientContacts),
    ...getVariablesFromContactSource(recipient.contact),
  };

  return variablesFromRecipients;
}

/** Return the first link for {{recipient.link}} */
function getVariablesForRecipientLinks(recipient: VisualRecipientModel): VariableInterpolationMap {
  if (recipient?.card?.links && recipient.card.links.length > 0) {
    return {
      [StaticVariableNamesEnum.RECIPIENT_LINK]: recipient.card.links[0].url,
    };
  }

  return {};
}

function getVariablesForRecipientNames(recipient: VisualRecipientModel): VariableInterpolationMap {
  const recipientName: string | undefined = getRecipientName(recipient, true);
  const recipientNames: NameParts | undefined = splitName(recipientName);

  return {
    [StaticVariableNamesEnum.RECIPIENT_NAME]: recipientName,
    [StaticVariableNamesEnum.RECIPIENT_GIVEN_NAME]: recipientNames && recipientNames.given,
    [StaticVariableNamesEnum.RECIPIENT_FAMILY_NAME]: recipientNames && recipientNames.family,
  };
}

function getVariablesFromContactSource(contact?: ContactModel): VariableInterpolationMap {
  if (!contact) {
    return {};
  }

  switch (contact.source) {
    case ContactSourcesEnum.EMAIL:
      return {[StaticVariableNamesEnum.RECIPIENT_EMAIL]: contact.handle};

    case ContactSourcesEnum.TWITTER:
      return {[StaticVariableNamesEnum.RECIPIENT_TWITTER]: contact.handle};

    case ContactSourcesEnum.PHONE:
      return {[StaticVariableNamesEnum.RECIPIENT_PHONE]: contact.handle};

    default:
      return {};
  }
}

function getVariablesFromOtherContacts(
  card?: CardModel,
  contacts?: ContactModel[],
): VariableInterpolationMap {
  if (!card || !contacts) {
    return {};
  }

  const sources = ['email', 'twitter', 'phone'];
  return sources.reduce((variables, source) => {
    const contactWithSource = contacts.find((contact) => contact.source === source);
    if (contactWithSource) {
      return {...variables, ...getVariablesFromContactSource(contactWithSource)};
    }

    return variables;
  }, {});
}

// We want to persist the survey variable so that it gets passed back to
// the backend where it can get replaced with an actual CSAT link
function getVariablesFromSurvey(): VariableInterpolationMap {
  return {[StaticVariableNamesEnum.SURVEY]: surveyTemplateVariable};
}
